/**
 * Browsers allow only a handful of WebGL contexts. Each `StreetViewPanorama`
 * (and the main Map) uses one. Creating a new panorama per property open
 * eventually steals the map’s context → blank white map, pins still visible.
 *
 * Reuse one panorama for the profile panel (inline ↔ fullscreen share it).
 */

type PanoOptions = {
  panoId: string
  heading: number
}

let panorama: google.maps.StreetViewPanorama | null = null
let canvas: HTMLDivElement | null = null
let owner: symbol | null = null

const baseOptions: google.maps.StreetViewPanoramaOptions = {
  addressControl: false,
  fullscreenControl: false,
  enableCloseButton: false,
  linksControl: true,
  panControl: true,
  zoomControl: true,
  motionTracking: false,
  motionTrackingControl: false,
}

export function attachSharedStreetViewPanorama(
  host: HTMLElement,
  streetViewLib: google.maps.StreetViewLibrary,
  opts: PanoOptions,
): () => void {
  const token = Symbol('street-view-owner')
  owner = token

  if (!canvas) {
    canvas = document.createElement('div')
    canvas.style.width = '100%'
    canvas.style.height = '100%'
  }

  if (canvas.parentElement !== host) {
    host.appendChild(canvas)
  }

  if (!panorama) {
    panorama = new streetViewLib.StreetViewPanorama(canvas, {
      ...baseOptions,
      pano: opts.panoId,
      pov: { heading: opts.heading, pitch: 0 },
      zoom: 1,
      visible: true,
    })
  } else {
    panorama.setPano(opts.panoId)
    panorama.setPov({ heading: opts.heading, pitch: 0 })
    panorama.setZoom(1)
    panorama.setVisible(true)
  }

  // Street View needs a layout pass after the host is shown/resized.
  requestAnimationFrame(() => {
    if (owner !== token || !panorama) return
    google.maps.event.trigger(panorama, 'resize')
  })

  return () => {
    if (owner !== token) return
    panorama?.setVisible(false)
    canvas?.remove()
    owner = null
  }
}
